import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from '../../context';
import { Empty, Failure, Loading } from '../../bits';
import { Chip, Panel } from '../../kit';
import { useT } from '../../../messages';
import type { IntakeUpload, IntakeUploads } from '../../../wire';
import { humanize, kilobytes } from './words';

/**
 * **Photos** — what the owner sent through the questionnaire, grouped by what
 * each pile is for (the template's shot list), as a grid to pick from.
 *
 * Moved out of the questionnaire card, where it was the last of seven drawers.
 * The actions are the old ones: **make one public** (copies it to the site's
 * public storage under a new name and gives its address, to paste into the
 * page), **set one aside**, and **copy the address** of one already public.
 *
 * Two "not set up" states, because they are two facts: a deployment with no
 * private photo storage has nothing to show at all, and one with no *public*
 * storage shows the photos and says why nothing can be made public. Neither
 * draws a button it cannot complete. The setting's name is on the sentence's
 * hover, for whoever runs the deployment, rather than in the copy.
 *
 * A photo that needs converting (an iPhone's HEIC) cannot be made public —
 * browsers will not show it — and the card says so on the photo.
 *
 * No questionnaire, no read: the card says where photos will come from.
 */
export function PhotosCard({
  tenantId,
  hasIntake,
}: {
  tenantId: string;
  hasIntake: boolean;
}): ReactNode {
  const t = useT();
  return (
    <Panel title={t('site.photos.title')} className="site-card site-card--wide">
      {hasIntake ? (
        <Uploads tenantId={tenantId} />
      ) : (
        <p className="muted">{t('site.photos.noForm')}</p>
      )}
    </Panel>
  );
}

function Uploads({ tenantId }: { tenantId: string }): ReactNode {
  const api = useApi();
  const t = useT();
  const page = useRead(() => api.intakeUploads(tenantId), [tenantId]);
  const action = useAction();

  const act = async (work: () => Promise<unknown>): Promise<void> => {
    const ok = await action.run(async () => {
      await work();
    });
    if (ok) page.reload();
  };

  const answer: IntakeUploads | null = page.value;
  const missing = answer?.missing_config.join(', ') ?? '';

  if (answer === null) {
    return (
      <>
        {page.failure !== null ? <Failure failure={page.failure} /> : null}
        {page.loading ? <Loading what={t('loading.photos')} /> : null}
      </>
    );
  }

  const groups = groupBySlot(answer.uploads);
  const stillNeeded = answer.slots.filter(
    (slot) =>
      slot.needed && !answer.uploads.some((u) => u.slot === slot.name && u.status !== 'rejected'),
  );

  return (
    <>
      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {action.failure !== null ? <Failure failure={action.failure} /> : null}

      {!answer.configured ? (
        <p className="warn" title={missing}>
          {t('site.photos.off')}
        </p>
      ) : !answer.promotion_configured ? (
        <p className="warn" title={missing}>
          {t('site.photos.noPublic')}
        </p>
      ) : null}

      {stillNeeded.length === 0 ? null : (
        <p className="muted">
          {t('site.photos.stillNeeded', {
            piles: stillNeeded.map((slot) => humanize(slot.name)).join(', '),
          })}
        </p>
      )}

      {answer.uploads.length === 0 ? (
        <Empty>{t('site.photos.empty')}</Empty>
      ) : (
        groups.map(([slot, uploads]) => (
          <section key={slot} className="site-pile">
            <h4 className="site-sub">
              {humanize(slot)}{' '}
              <span className="muted">{t('site.photos.pileCount', { count: uploads.length })}</span>
              {answer.slots.find((s) => s.name === slot)?.needed === true ? (
                <Chip tone="plain">{t('site.photos.forPage')}</Chip>
              ) : null}
            </h4>
            <ul className="site-photos">
              {uploads.map((upload) => (
                <Photo
                  key={upload.upload_id}
                  upload={upload}
                  canPromote={answer.promotion_configured}
                  busy={action.busy}
                  onPromote={() =>
                    void act(() => api.promoteIntakeUpload(tenantId, upload.upload_id))
                  }
                  onReject={() =>
                    void act(() => api.rejectIntakeUpload(tenantId, upload.upload_id))
                  }
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </>
  );
}

function groupBySlot(uploads: readonly IntakeUpload[]): Array<[string, IntakeUpload[]]> {
  const groups = new Map<string, IntakeUpload[]>();
  for (const upload of uploads) {
    const list = groups.get(upload.slot) ?? [];
    list.push(upload);
    groups.set(upload.slot, list);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function Photo({
  upload,
  canPromote,
  busy,
  onPromote,
  onReject,
}: {
  upload: IntakeUpload;
  canPromote: boolean;
  busy: boolean;
  onPromote: () => void;
  onReject: () => void;
}): ReactNode {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = (url: string): void => {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const format = upload.content_type.split('/')[1]?.toUpperCase() ?? upload.content_type;

  return (
    <li className={`site-photo${upload.status === 'rejected' ? ' is-rejected' : ''}`}>
      {upload.view_url === null ? (
        <div className="site-photo__frame site-photo__frame--blank" aria-hidden="true" />
      ) : (
        <img className="site-photo__frame" src={upload.view_url} alt={upload.original_name} />
      )}
      <div className="site-photo__words">
        <span className="muted">
          {kilobytes(upload.bytes)}
          {upload.width === null || upload.height === null
            ? ''
            : ` · ${String(upload.width)}×${String(upload.height)}`}
          {` · ${format}`}
        </span>
        {upload.needs_converting ? (
          <Chip tone="warn" title={t('site.photos.convertWhy')}>
            {t('site.photos.convert')}
          </Chip>
        ) : null}
        {upload.status === 'rejected' ? <Chip>{t('site.photos.setAside')}</Chip> : null}

        {upload.status === 'promoted' && upload.promoted_url !== null ? (
          <>
            <Chip tone="ok">{t('site.photos.public')}</Chip>
            <code className="site-photo__url">{upload.promoted_url}</code>
            <button
              type="button"
              className="button button--sm"
              onClick={() => copy(upload.promoted_url ?? '')}
            >
              {copied ? t('once.copied') : t('site.photos.copyUrl')}
            </button>
          </>
        ) : upload.status === 'rejected' ? null : (
          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={!canPromote || upload.needs_converting || busy}
              onClick={onPromote}
            >
              {t('site.photos.promote')}
            </button>
            <button
              type="button"
              className="button button--quiet button--sm"
              disabled={busy}
              onClick={onReject}
            >
              {t('site.photos.reject')}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
